import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import {
  VOYAGE_LOOKUP_CREDITS,
  VOYAGE_NEARBY_CREDITS,
  VOYAGE_POI_RADIUS_KM,
} from 'src/app/services/operators/voyageradar-credit-actions';
import { VoyageTrackController } from '../toolbar/track.controller';

/**
 * Voyage Radar nearby-vessel service — drives the radar button and its drawer.
 *
 * Sibling of `VoyagePortCallsService` and deliberately its near-copy: same
 * subject/fetchedFor state machine, same three map-owned callbacks, same
 * free-re-open-from-the-stored-record rule. Port calls answer *where has this
 * ship been*; this answers the other half of the same question, *who is around
 * it right now*, and the two drawers are meant to read as one product.
 *
 * Three things it does that port calls does not, all of them consequences of
 * the endpoint rather than of taste:
 *
 *  - IT CARRIES A RADIUS. `/v1/vessels/nearby` is a disc, and the operator
 *    chooses how wide. That makes the radius part of the question asked and
 *    therefore part of the answer stored, and it is why an empty result here is
 *    a real answer ("nothing within 5 km") rather than the absence of one.
 *  - IT SORTS AND FILTERS AFTER THE FACT. The fetch buys up to 200 rows; nothing
 *    about the radius bought says the user wants to look at all of them at once.
 *    `maxKm` and `direction` below are pure view state — they never re-fetch and
 *    never cost anything.
 *  - A ROW HAS NO POSITION. The endpoint returns none, so a row is a name and a
 *    distance until someone buys its fix. `onPickVessel` is that purchase, and
 *    it belongs to the map controller because only it has a map and a wallet.
 *
 * Like its sibling this is NOT a `PlaybackController`: it is injected straight
 * into its control component rather than handed out as `context.controller`, so
 * the base class's transport methods would buy nothing and its `state$` could
 * not be satisfied honestly. That is also why it is a `Service` — see the note
 * on `VoyagePortCallsService` for the three-way split the suffix records.
 */

// `NearbyVesselRow` — "one stored row, exactly the six fields the operator gives
// us" — was declared here and referenced by nothing, not even this file. The
// stored rows are read straight off the record as `any` (see `buildNearbyRows`),
// so the interface documented a shape nothing was ever checked against. Removed
// rather than kept as a type that looks load-bearing and is not; the display
// shape below IS used, and is the one worth trusting.

/** A row shaped for display. */
export interface NearbyRowView {
  mmsi: string;
  /** Never blank — falls back to the MMSI, which is the one thing always present. */
  name: string;
  shipType: string | null;
  /** '3.4 km' / '820 m', or null when the operator omitted the distance. */
  distance: string | null;
  /** Raw kilometres, kept for the slider and the sort. */
  distanceKm: number | null;
  /** '11.4 kn', or null for a silent or sentinel value. */
  speed: string | null;
  /** '271°', or null for a silent or sentinel value. */
  course: string | null;
  /** Already drawn on the map — the row offers a free fly-to rather than a buy. */
  onMap: boolean;
  /** This row's lookup is in flight. */
  pending: boolean;
}

export type NearbyDirection = 'nearest' | 'farthest';

export interface NearbyState {
  /**
   * The hull this drawer is ABOUT. Same rule as port calls: the subject follows
   * the selection whenever that changes, and can also be named outright by a
   * caller that knows whose card was clicked.
   *
   * BOTH halves are load-bearing, and each has been the whole feature on its own
   * at some point:
   *
   * - The popup's button names the MMSI outright, off the clicked card. Four
   *   layers open that card and the located-vessel one selects nothing, so a
   *   button that consulted the selection would buy nothing there.
   * - The toolbar button has no card to read and can only follow the selection.
   *   That path was dead until the component's `providers` list gained this
   *   controller: `providedIn: 'root'` had it resolving its own
   *   `VoyageTrackController` in the root injector — a second instance, which no
   *   map click ever wrote to — so the drawer read "Select a vessel on the map"
   *   with a vessel plainly selected. See the note on that providers list.
   */
  subjectMmsi: string | null;
  subjectName: string | null;
  /** MMSI the current `rows` belong to — distinguishes "held" from "not fetched". */
  fetchedForMmsi: string | null;
  rows: NearbyRowView[];
  /**
   * The radius the stored answer was BOUGHT at, echoed by the server as the one
   * actually served. Never the value the user typed: the two differ whenever the
   * platform clamped, and a list must not be labelled with a scope it does not
   * have.
   */
  radiusKm: number | null;
  /** Rows after the subject hull was filtered out of its own answer. */
  count: number;
  /** More vessels were in range than the record keeps. */
  truncated: boolean;
  /** `fetchedAt` of the stored record, for "fetched 6 d ago". */
  fetchedAt: string | null;
  loading: boolean;
  /** The radius the INPUT currently holds — the next question, not the last answer. */
  inputRadiusKm: number;
  /** View-only cutoff (§2.7). Never re-fetches, never charges. */
  maxKm: number;
  /** View-only ordering (§2.7). Never re-fetches, never charges. */
  direction: NearbyDirection;
  /**
   * Price and balance live HERE, pushed by the map controller, because a toolbar
   * control cannot reach a sensor — `ToolbarControlContext` is
   * `{ controller, slot, options }` and carries no sensor row.
   */
  nearbyCredits: number;
  /**
   * What ONE ROW costs to place — the ordinary vessel lookup, not this drawer's
   * own search. Two prices live here because the drawer sells two different
   * things: `nearbyCredits` buys the list, and this buys a position for one hull
   * in it.
   *
   * Needed as state rather than as a constant because clicking a row is now the
   * whole affordance (the explicit per-row Locate button is gone, following the
   * POI card), and a click that spends has to name its price before it is made.
   */
  lookupCredits: number;
  creditsRemaining: number | null;
  /** TOI over — nothing here can be bought again. See `hasVoyageToiFinished`. */
  toiEnded: boolean;
}

/**
 * What the drawer asks for when the operator has expressed no preference.
 *
 * 10 km, not the platform's 1000. A tasked POINT has no other input, so its
 * refresh takes the widest disc the operator serves; around a VESSEL that is the
 * wrong question — a 1000 km disc off Singapore returns the row cap sorted by
 * distance and says nothing about who is *with* this ship.
 */
export const NEARBY_DEFAULT_RADIUS_KM = 10;

export const EMPTY_NEARBY_STATE: NearbyState = {
  subjectMmsi: null,
  subjectName: null,
  fetchedForMmsi: null,
  rows: [],
  radiusKm: null,
  count: 0,
  truncated: false,
  fetchedAt: null,
  loading: false,
  inputRadiusKm: NEARBY_DEFAULT_RADIUS_KM,
  maxKm: NEARBY_DEFAULT_RADIUS_KM,
  direction: 'nearest',
  nearbyCredits: VOYAGE_NEARBY_CREDITS,
  lookupCredits: VOYAGE_LOOKUP_CREDITS,
  creditsRemaining: null,
  toiEnded: false,
};

/**
 * AIS says "I am not telling you" with a magic number rather than an absence, so
 * both sentinels have to be recognised or the drawer prints 102.3 kn for a
 * vessel that simply did not report. 102.3 is the SOG sentinel and 360 the COG
 * one; both are also out of physical range, so testing the bound catches a
 * garbled value at the same time.
 */
const SOG_UNKNOWN = 102.3;
const COG_UNKNOWN = 360;

const num = (v: any): number | null => {
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
};

/** '820 m' below a kilometre, '3.4 km' above it — the precision people read at. */
function formatDistance(km: number | null): string | null {
  if (km === null) return null;
  if (km < 1) return `${Math.round(km * 1000)} m`;
  return `${km < 10 ? km.toFixed(1) : Math.round(km)} km`;
}

/**
 * Stored record → display rows.
 *
 * Pure and exported so the spec can exercise every row state without standing up
 * the service — the same reason `buildPortCallRows` and
 * `buildVoyagePoiNearbyRows` are exported.
 *
 * Order is the operator's own distance sort and is PRESERVED here rather than
 * re-derived: it is the order the rows were bought in, and `filterAndSortNearbyRows`
 * is what re-orders them for display.
 */
export function buildNearbyRows(
  record: any,
  ctx?: { onMap?: (mmsi: string) => boolean; pendingMmsi?: string | null },
): NearbyRowView[] {
  const rows: any[] = Array.isArray(record?.vessels) ? record.vessels : [];
  return rows.map((v: any) => {
    const mmsi = String(v?.mmsi ?? '');
    const sog = num(v?.sog);
    const cog = num(v?.cog);
    const distanceKm = num(v?.distanceKm);
    return {
      mmsi,
      // A silent hull reports no name, and the MMSI is the one field that is
      // always there — a blank row could not be acted on or even talked about.
      name: (v?.name != null && String(v.name).trim() !== '' ? String(v.name) : mmsi),
      shipType: v?.shipType != null && String(v.shipType).trim() !== '' ? String(v.shipType) : null,
      distance: formatDistance(distanceKm),
      distanceKm,
      speed: sog === null || sog >= SOG_UNKNOWN ? null : `${sog} kn`,
      course: cog === null || cog >= COG_UNKNOWN ? null : `${Math.round(cog)}°`,
      onMap: ctx?.onMap ? ctx.onMap(mmsi) : false,
      pending: ctx?.pendingMmsi != null && String(ctx.pendingMmsi) === mmsi,
    };
  });
}

/**
 * The post-load view controls, in one pure function (plan §2.7).
 *
 * SORTED, not reversed. `reverse()` is only "farthest first" if the array is
 * strictly ascending, and it is not: rows with no distance are deliberately kept
 * (below), so reversing floats every unknown-distance hull to the TOP of the
 * farthest view — the most prominent rows being the ones with no distance at
 * all. Nulls are pinned last in BOTH directions instead: they are unplaced,
 * neither near nor far.
 *
 * A row with no `distanceKm` is KEPT rather than dropped, by the same rule every
 * other optional field follows here — a slider cutoff must not read as "this
 * vessel isn't there".
 */
export function filterAndSortNearbyRows<T extends { distanceKm?: number | null }>(
  rows: T[],
  opts: { maxKm: number; direction: NearbyDirection },
): T[] {
  const cut = (Array.isArray(rows) ? rows : []).filter(
    (r) => r.distanceKm == null || r.distanceKm <= opts.maxKm,
  );
  const dir = opts.direction === 'farthest' ? -1 : 1;
  return cut.slice().sort((a, b) => {
    if (a.distanceKm == null) return b.distanceKm == null ? 0 : 1;
    if (b.distanceKm == null) return -1;
    return (a.distanceKm - b.distanceKm) * dir;
  });
}

@Injectable({ providedIn: 'root' })
export class VoyageNearbyService {
  private _state$ = new BehaviorSubject<NearbyState>({ ...EMPTY_NEARBY_STATE });
  readonly state$: Observable<NearbyState> = this._state$.asObservable();

  /**
   * Scope for the remembered radius, pushed by the map controller.
   *
   * Keyed by task AND sensor, because a task may carry two Voyage Radar sensors
   * — which is exactly why the server refuses to guess between them — and a
   * task-only key would let one sensor's radius answer for the other's drawer.
   */
  private storageKey: string | null = null;

  constructor(private track: VoyageTrackController) {
    // The same selection event the Trace button rides, for the same reason: every
    // marker click already updates `VoyageTrackController`, so this drawer needs
    // no second notification. A change to a DIFFERENT hull resets the rows;
    // re-selecting the SAME one is a no-op, so an in-flight fetch is never
    // interrupted by its own marker staying selected.
    this.track.state$.subscribe((ts) =>
      this.setSubject(ts.activeMmsi ?? null, ts.activeVesselName ?? null),
    );
  }

  // ── Callbacks the map controller assigns in attach() ────────────────────────

  /** Read `sensor.nearbyHistory` for this mmsi, or null. Free — no request. */
  onLookupStored: ((mmsi: string) => any | null) | null = null;

  /** The paid fetch — the map controller owns the HTTP call and the wallet. */
  onFetch: ((mmsi: string, vesselName: string | null, radiusKm: number) => void) | null = null;

  /** Assigned by whichever control is mounted, so the popup action can open us. */
  onRequestOpen: (() => void) | null = null;

  /**
   * A row click: place that hull on the map for 1 credit.
   *
   * Assigned by the map controller because the drawer has neither a map nor a
   * wallet. Optional for the same reason it is optional on the map side — an
   * unclaimed seam costs the feature its row click, not the app its build.
   */
  onPickVessel: ((mmsi: string, onSettled?: () => void) => void) | null = null;

  // ── Scope and view state ────────────────────────────────────────────────────

  /**
   * Told which task and sensor this drawer is drawing for, so the remembered
   * radius is scoped to both. Called by the map controller; until it is, the
   * radius simply falls back to its default rather than being remembered under
   * a key that could belong to the wrong sensor.
   */
  setStorageScope(taskId: string | null, sensorId: string | null): void {
    const key =
      taskId && sensorId ? `voyageRadarNearbyRadiusKm_task_${taskId}_sensor_${sensorId}` : null;
    // Guarded on an actual CHANGE of scope, and that guard is load-bearing
    // rather than an optimisation. The map controller pushes this alongside the
    // credit lines, which it re-pushes after every spend — so re-reading the
    // stored radius unconditionally would overwrite whatever the operator had
    // just typed into the input with whatever the last fetch happened to save.
    if (key === this.storageKey) return;
    this.storageKey = key;
    const remembered = this.readRememberedRadius();
    if (remembered !== null) this.patch({ inputRadiusKm: remembered });
  }

  /** The number input. View state only — nothing is bought until `fetch` runs. */
  setInputRadiusKm(km: number): void {
    const n = Number(km);
    if (!Number.isFinite(n) || n <= 0) return;
    this.patch({ inputRadiusKm: n });
  }

  /** The distance slider (§2.7). Never re-fetches, never charges. */
  setMaxKm(km: number): void {
    const n = Number(km);
    if (!Number.isFinite(n) || n < 0) return;
    this.patch({ maxKm: n });
  }

  /** The nearest/farthest toggle (§2.7). Never re-fetches, never charges. */
  setDirection(direction: NearbyDirection): void {
    this.patch({ direction });
  }

  /** The rows currently ON SHOW — held rows put through the two view controls. */
  visibleRows(): NearbyRowView[] {
    const s = this._state$.value;
    return filterAndSortNearbyRows(s.rows, { maxKm: s.maxKm, direction: s.direction });
  }

  // ── Subject ─────────────────────────────────────────────────────────────────

  /**
   * Point the drawer at a hull and show whatever is already held for it.
   *
   * The stored read is FREE — it looks at the record already on the sensor
   * document, no request — which is what makes re-opening a bought list cost
   * nothing and need no click.
   */
  setSubject(mmsi: string | null, name: string | null): void {
    const current = this._state$.value;
    if (mmsi === current.subjectMmsi) {
      // Same hull — keep the rows, but take a better name if one turned up. A
      // popup click knows the MMSI before it knows the name.
      if (name && name !== current.subjectName) this.patch({ subjectName: name });
      return;
    }
    const stored = mmsi ? this.onLookupStored?.(mmsi) : null;
    this.patch({
      ...EMPTY_NEARBY_STATE,
      ...this.creditsOnly(),
      ...this.viewOnly(),
      subjectMmsi: mmsi,
      subjectName: name,
      ...(stored ? this.rowsFromRecord(stored, mmsi) : {}),
    });
  }

  // ── Actions ─────────────────────────────────────────────────────────────────

  /**
   * The popup's Nearby action: point the drawer at this hull, open it, fetch.
   *
   * `mmsi` is passed EXPLICITLY, from the clicked card's own `data-mmsi`. That
   * is the whole fix inherited from port calls: reading
   * `VoyageTrackController.activeMmsi` here is null for every popup except the
   * playback layer's, so the button opened the drawer and bought nothing. The
   * card knows whose it is; nothing else has to agree.
   *
   * Always fetches, even when a record is held — a user pressing a button
   * labelled `2 credits` is asking about the vessel now, and quietly returning a
   * week-old answer because one exists is the wrong default for a priced
   * control. Free re-opening still happens on its own whenever the selection
   * changes.
   */
  openAndFetch(mmsi?: string | null, vesselName?: string | null, radiusKm?: number): void {
    if (!this.onRequestOpen) return;

    const subject = mmsi ?? this.track.snapshot().activeMmsi ?? null;
    this.setSubject(subject, vesselName ?? this.subjectNameFor(subject));

    this.onRequestOpen();

    const state = this._state$.value;
    if (!state.subjectMmsi || state.loading) return;
    this.fetch(state.subjectMmsi, state.subjectName, radiusKm ?? state.inputRadiusKm);
  }

  /** Keep the name already held for this hull rather than blanking the title. */
  private subjectNameFor(mmsi: string | null): string | null {
    const s = this._state$.value;
    if (mmsi && s.subjectMmsi === mmsi) return s.subjectName;
    const active = this.track.snapshot();
    return active.activeMmsi === mmsi ? active.activeVesselName ?? null : null;
  }

  /** The 2-credit spend. Fires on click; the cost is shown on the control. */
  fetch(
    mmsi: string | null | undefined,
    vesselName: string | null | undefined,
    radiusKm?: number,
  ): void {
    if (!mmsi) return;
    const asked = Number.isFinite(Number(radiusKm))
      ? Number(radiusKm)
      : this._state$.value.inputRadiusKm;
    this.patch({ loading: true, inputRadiusKm: asked });
    this.onFetch?.(mmsi, vesselName ?? null, asked);
  }

  /** A row click — 1 credit, places the hull and flies to it. */
  pickVessel(mmsi: string): void {
    if (!mmsi || !this.onPickVessel) return;
    this.patch({ rows: this.markPending(mmsi, true) });
    this.onPickVessel(mmsi, () => this.patch({ rows: this.markPending(mmsi, false) }));
  }

  /** Called by the map controller once a fetch resolves — success or empty. */
  setFetched(mmsi: string, record: any): void {
    this.patch({ loading: false, ...this.rowsFromRecord(record, mmsi) });
    this.rememberRadius(this._state$.value.inputRadiusKm);
  }

  setFailed(): void {
    this.patch({ loading: false });
  }

  /**
   * Pushed by the map controller, the same way `VoyageTrackController` is told.
   *
   * BOTH prices, in one call, for the same reason the balance travels with them:
   * the drawer quotes the search price on its primary button and the row price
   * in its footer, and a caller that could refresh one without the other would
   * eventually show two figures read at different moments.
   */
  setCredits(
    creditsRemaining: number | null,
    nearbyCredits: number,
    lookupCredits: number,
    toiEnded: boolean,
  ): void {
    this.patch({ creditsRemaining, nearbyCredits, lookupCredits, toiEnded });
  }

  /** Re-render the On map badges without a refetch, after a row was placed. */
  refreshOnMap(onMap: (mmsi: string) => boolean): void {
    this.patch({ rows: this._state$.value.rows.map((r) => ({ ...r, onMap: onMap(r.mmsi) })) });
  }

  snapshot(): NearbyState {
    return this._state$.value;
  }

  // ── Internals ───────────────────────────────────────────────────────────────

  /**
   * An empty record is still an ANSWER for this hull, and this is the one place
   * the drawer departs from port calls (§2.4).
   *
   * `fetchedForMmsi` is set either way, so a count-0 record renders through the
   * ROWS state reading "nothing within N km" rather than dropping back to "not
   * fetched" and re-offering a spend that already happened. With the radius
   * under the operator's control an empty answer is a routine, correct outcome —
   * 5 km in open water — not a failure to find anything.
   *
   * The slider's cutoff is re-seeded to the radius SERVED, so re-opening a
   * record never shows a narrowed view the user did not ask for, and never
   * implies rows exist beyond what was bought.
   */
  private rowsFromRecord(record: any, mmsi: string | null | undefined): Partial<NearbyState> {
    const named = record?.vesselName ? String(record.vesselName) : null;
    const served = num(record?.radiusKm);
    const rows = buildNearbyRows(record);
    return {
      fetchedForMmsi: mmsi ?? (record?.mmsi != null ? String(record.mmsi) : null),
      rows,
      radiusKm: served,
      count: Number.isFinite(record?.count) ? record.count : rows.length,
      truncated: record?.truncated === true,
      fetchedAt: record?.name ?? null,
      maxKm: served ?? this._state$.value.inputRadiusKm,
      ...(named ? { subjectName: named } : {}),
    };
  }

  private markPending(mmsi: string, pending: boolean): NearbyRowView[] {
    return this._state$.value.rows.map((r) => (r.mmsi === mmsi ? { ...r, pending } : r));
  }

  /**
   * Price/balance survive a reset — they belong to the sensor, not the hull.
   * BOTH prices: dropping `lookupCredits` here would put the shipped fallback
   * back in the row footer on every change of selection, which is only ever
   * corrected by the next `syncCreditLines` happening to run.
   */
  private creditsOnly(): Partial<NearbyState> {
    const { nearbyCredits, lookupCredits, creditsRemaining } = this._state$.value;
    return { nearbyCredits, lookupCredits, creditsRemaining };
  }

  /**
   * The radius the operator last typed survives a change of hull too — asking a
   * second vessel the same question by default is the point of remembering it.
   * `direction` is a reading preference and survives for the same reason;
   * `maxKm` does not, because it is re-seeded from whatever record loads next.
   */
  private viewOnly(): Partial<NearbyState> {
    const { inputRadiusKm, direction } = this._state$.value;
    return { inputRadiusKm, direction };
  }

  private readRememberedRadius(): number | null {
    if (!this.storageKey) return null;
    try {
      const n = Number(localStorage.getItem(this.storageKey));
      return Number.isFinite(n) && n > 0 ? n : null;
    } catch {
      // A blocked or full localStorage must never cost the drawer its fetch.
      return null;
    }
  }

  private rememberRadius(km: number): void {
    if (!this.storageKey || !Number.isFinite(km) || km <= 0) return;
    try {
      localStorage.setItem(this.storageKey, String(km));
    } catch {
      /* see above — remembering is a convenience, never a precondition */
    }
  }

  private patch(patch: Partial<NearbyState>): void {
    this._state$.next({ ...this._state$.value, ...patch });
  }
}

/**
 * The ceiling the input offers before any answer has come back.
 *
 * Re-exported here so the panel needs one import rather than two, and named for
 * what it is at this seam: a pre-first-answer fallback only. Every response
 * echoes the radius actually served and THAT value wins, so lowering the
 * platform radius takes effect without a client release.
 */
export const NEARBY_FALLBACK_MAX_KM = VOYAGE_POI_RADIUS_KM;
